import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSearch } from "../../../contexts/SearchContext";
import { useSelectedResult } from "../../../contexts/SelectedResultContext";
import { useFlightOfferUi } from "../../../contexts/FlightOfferUiContext";
import { useBookingFlow } from "../../../Components/FlightBookingFlowMockup/flightBookingMockup/hooks/useBookingFlow";
import { applyResolvedFlightWizardBack } from "../../../Components/FlightBookingFlowMockup/flightBookingMockup/wizard/flightWizardBackStep";
import { buildDetailsWave1bPresentation } from "./detailsWave1b";

/**
 * Shared live inputs for V2 stages. Presentation stays a ViewModel.
 * Booking writes stay on the existing booking-flow handlers.
 */
export function useLiveFlightStage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const routeParams = useParams();
  const { setFlightSearchEditorOpen } = useFlightOfferUi();
  const booking = useBookingFlow();
  const search = useSearch();
  const { handleFeeData } = useSelectedResult();

  const presentation = useMemo(
    () =>
      buildDetailsWave1bPresentation({
        selectedFlightOffer: booking.selectedFlightOffer,
        pricing: booking.pricing,
        bookingExchangeRateSnapshot: null,
        searchFields: {
          origin: search.origin,
          location: search.location,
          calendarDateArrival: search.calendarDateArrival,
          calendarDateLeave: search.calendarDateLeave,
          roomInfo: search.roomInfo,
          flightTripType: search.flightTripType,
        },
        handleFeeData,
        canAdvance: booking.canAdvanceFromCurrentStep(),
      }),
    [
      booking,
      search.origin,
      search.location,
      search.calendarDateArrival,
      search.calendarDateLeave,
      search.roomInfo,
      search.flightTripType,
      handleFeeData,
    ]
  );

  const onBack = useCallback(() => {
    applyResolvedFlightWizardBack({
      currentStep: booking.currentStep,
      selectedFlightOffer: booking.selectedFlightOffer,
      searchGate: booking.searchGate,
      goToStep: booking.goToStep,
      navigate,
      pathname,
      routeParams,
      setFlightSearchEditorOpen,
    });
  }, [
    booking,
    navigate,
    pathname,
    routeParams,
    setFlightSearchEditorOpen,
  ]);

  return {
    booking,
    search,
    presentation,
    onBack,
    offer: presentation?.offer || null,
    searchVm: presentation?.search || null,
  };
}
